import {useCallback, useMemo, useState} from "react";
import {toKatakana} from "@sakura-grp/lib";
import {isValidKana} from "@/lib/api/business-cards";

export const KANA_ERROR_MESSAGE = "フリガナはカタカナで入力してください";

export interface KanaFieldState {
  /**
   * 入力確定後の真の検証エラー（null = 有効）。
   * 送信可否（canSave）の判定に使う。IME 変換中でも値そのものを評価する。
   */
  error: string | null;
  /**
   * 画面表示用エラー。IME 変換中（compositionstart 〜 compositionend）は抑止する。
   * 変換確定前の中間文字（カタカナにならないローマ字残り等）で error が立っても
   * 表示しないことで、入力欄が動く（レイアウトシフト）のを防ぐ。
   */
  visibleError: string | null;
  /**
   * フリガナ Input に spread する IME 安全ハンドラ。
   * compositionend を取りこぼしても onBlur で変換中フラグを必ず落とす。
   */
  inputProps: {
    onCompositionStart: () => void;
    onCompositionEnd: () => void;
    onBlur: () => void;
  };
  /**
   * 変換中フラグを明示的に初期化する。フォーム（drawer）を開き直したときに呼び、
   * 「閉じる際に compositionend を取りこぼしてフラグが true のまま残り、再オープン後に
   * エラーが出なくなる」ラッチを防ぐ。
   */
  reset: () => void;
}

/**
 * フリガナ入力の検証と IME 変換中のエラー表示抑止を担うフック。
 *
 * ひらがな入力は toKatakana で正規化したうえでカタカナ妥当性を判定する。
 * 日本語入力（IME）の変換確定前はキーストロークごとに onChange が中間文字を
 * 流すため、それを都度検証すると一瞬だけエラーが点滅し、エラー行の出し入れで
 * 入力欄がガタつく。compositionstart/compositionend で変換中を検知し、
 * 変換中は visibleError を抑止する（真の error は canSave 用に維持）。
 */
export function useKanaField(kana: string): KanaFieldState {
  const [composing, setComposing] = useState(false);

  const error = useMemo(() => {
    const trimmed = kana.trim();
    if (trimmed.length === 0) return null;
    return isValidKana(toKatakana(trimmed)) ? null : KANA_ERROR_MESSAGE;
  }, [kana]);

  const reset = useCallback(() => setComposing(false), []);

  return {
    error,
    visibleError: composing ? null : error,
    inputProps: {
      onCompositionStart: () => setComposing(true),
      onCompositionEnd: () => setComposing(false),
      onBlur: () => setComposing(false),
    },
    reset,
  };
}
